import EmptyDottedIcon from '@assets/icons/empty-dotted.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { BusinessInfoCard } from '@/experiences/sales-portal/domain/prospects/screens/prospect-finder/components/business-info-card';
import { createStyles } from '@/contexts/theme-context';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { FlatList, ListRenderItem, Platform, View } from 'react-native';
import { NearbyBusiness } from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { Spinner } from '@usepepper/sprout-kit-ui';

const END_REACHED_THRESHOLD = 0.5;

interface Props {
  claimingYelpIds?: ReadonlySet<string>;
  error?: ApolloError;
  fetchingMore: boolean;
  fetchMore: () => void;
  nearbyBusinesses?: NearbyBusiness[];
  onClaim?: (business: NearbyBusiness) => void;
  onRowPress?: (business: NearbyBusiness) => void;
  retry: () => void;
  selectedYelpId?: string;
  supplierCountryCode: string | undefined;
}

const keyExtractor = (item: NearbyBusiness) => item.yelpBusinessId;

export const ProspectsResultsList: React.FC<Props> = ({
  claimingYelpIds,
  error,
  fetchMore,
  fetchingMore,
  nearbyBusinesses,
  onClaim,
  onRowPress,
  retry,
  selectedYelpId,
  supplierCountryCode,
}) => {
  const [styles, theme] = useStyles();
  const listRef = useRef<FlatList<NearbyBusiness>>(null);

  // Scroll the selected row into view (pin clicks select from the map);
  // only on selection changes so pagination appends don't re-scroll.
  const prevSelectedYelpId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const selectionChanged = selectedYelpId !== prevSelectedYelpId.current;
    prevSelectedYelpId.current = selectedYelpId;
    if (!selectionChanged || !selectedYelpId || !nearbyBusinesses) {
      return;
    }
    const index = nearbyBusinesses.findIndex(
      (business) => business.yelpBusinessId === selectedYelpId,
    );
    if (index >= 0) {
      listRef.current?.scrollToIndex({
        animated: false,
        index,
        viewPosition: 0.5,
      });
    }
  }, [selectedYelpId, nearbyBusinesses]);

  if (!supplierCountryCode || (error && !nearbyBusinesses?.length)) {
    return (
      <ErrorState
        description={i18n.t('sales_portal.prospect_finder.results.error')}
        onPress={() => retry()}
        title={i18n.t('sales_portal.error.title')}
      />
    );
  }

  const renderItem: ListRenderItem<NearbyBusiness> = ({ item }) => (
    <BusinessInfoCard
      business={item}
      claiming={claimingYelpIds?.has(item.yelpBusinessId)}
      onClaimPress={onClaim}
      onRowPress={onRowPress}
      selected={item.yelpBusinessId === selectedYelpId}
      supplierCountryCode={supplierCountryCode}
    />
  );

  return (
    <FlatList
      contentContainerStyle={styles.listContent}
      data={nearbyBusinesses ?? []}
      keyExtractor={keyExtractor}
      ListEmptyComponent={
        <EmptyState
          iconElement={<EmptyDottedIcon stroke={theme.sk.color.icon.primary} />}
          title={i18n.t('sales_portal.prospect_finder.results.empty')}
        />
      }
      ListFooterComponent={
        fetchingMore ? (
          <View style={styles.footer}>
            <Spinner />
          </View>
        ) : null
      }
      onEndReached={fetchMore}
      onEndReachedThreshold={END_REACHED_THRESHOLD}
      // Virtualized rows may not be measured yet — skip if so.
      onScrollToIndexFailed={() => undefined}
      ref={listRef}
      renderItem={renderItem}
      style={styles.list}
    />
  );
};

const useStyles = createStyles((theme) => ({
  footer: {
    alignItems: 'center',
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
  list: {
    flex: 1,
    ...Platform.select({
      web: {
        scrollbarColor: `${theme.sk.color.bg.scrollbar} transparent`,
        scrollbarWidth: 'thin',
      },
    }),
  },
  // centers the empty state component but does not affect layout when rows present
  listContent: {
    flexGrow: 1,
  },
}));
